// src/pages/Tenant_Add.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import { useNavigate } from "react-router-dom";
import {
  Database,
  UploadCloud,
  Save,
  XCircle,
  Hash,
  Globe,
  CalendarDays,
  Users,
  ShieldCheck,
  Server,
} from "lucide-react";

const PRODUCTS = [
  { id: "PATROL", label: "EBD Patrol" },
  { id: "VISIT", label: "Patrol Visit" },
  { id: "SENTINEL", label: "Sentinel" },
];

const PRODUCT_INFO = {
  PATROL: {
    short: "Patrol",
    long: "EBD Patrol",
    dbPrefix: "patrol_t_",
    domain: "omega.patrol.app",
    dbEngine: "MSSQL",
    accept: ".sql",
    uploadText: "Server template will be used automatically",
    usesServerTemplate: true,
  },
  VISIT: {
    short: "Patrol Visit",
    long: "Patrol Visit (Visit Module)",
    dbPrefix: "patrolv_t_",
    domain: "omega.visit.patrol.app",
    dbEngine: "MYSQL",
    accept: ".sql,.sql.gz",
    uploadText: "Upload a MySQL/MariaDB .sql or .sql.gz template",
    usesServerTemplate: false,
  },
  SENTINEL: {
    short: "Sentinel",
    long: "Sentinel",
    dbPrefix: "sentinel_t_",
    domain: "omega.sentinel.app",
    dbEngine: "MSSQL",
    accept: ".sql",
    uploadText: "Server template will be used automatically",
    usesServerTemplate: true,
  },
};

const PLANS = ["STARTER", "PRO", "ENTERPRISE"];

const uc = (s) => s.toUpperCase().replace(/[^A-Z0-9_]/g, "");
const DB_OK = (s) => /^[A-Za-z0-9_]{1,64}$/.test(s);

const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = n > 0 ? Math.floor(Math.log(n) / Math.log(1024)) : 0;
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const DEFAULT_FEATURES = JSON.stringify(
  { modules: ["vehicle", "inventory"] },
  null,
  2
);

const ALLOWED = new Set(["vehicle", "inventory"]);

export default function Tenant_Add() {
  const nav = useNavigate();

  const [file, setFile] = useState(null);

  const [form, setForm] = useState({
    tenantCode: "",
    name: "",
    product: "PATROL",
    databaseName: "",
    domain: "",
    plan: "STARTER",
    maxUsers: 5,
    expiresOn: "",
    graceDays: 7,
    featuresJson: DEFAULT_FEATURES,
  });

  const [phase, setPhase] = useState("idle");
  const [pct, setPct] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [eta, setEta] = useState(null);

  const controllerRef = useRef(null);
  const tickRef = useRef({ t0: 0, lastT: 0, lastLoaded: 0 });

  const disabled = useMemo(() => phase !== "idle", [phase]);

  const productInfo = PRODUCT_INFO[form.product] || PRODUCT_INFO.PATROL;
  const needsUpload = !productInfo.usesServerTemplate;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!form.tenantCode) {
      setForm((f) => ({ ...f, databaseName: "" }));
      return;
    }

    const selectedProduct = PRODUCT_INFO[form.product] || PRODUCT_INFO.PATROL;
    const lower = form.tenantCode.toLowerCase();

    setForm((f) => ({
      ...f,
      databaseName: `${selectedProduct.dbPrefix}${lower}`,
    }));
  }, [form.tenantCode, form.product]);

  useEffect(() => {
    setFile(null);
  }, [form.product]);

  function parseAndNormalizeFeatures(raw) {
    try {
      const obj = JSON.parse(raw || "{}");
      const mods = Array.isArray(obj.modules)
        ? obj.modules.filter((m) => ALLOWED.has(m))
        : [];

      const finalMods = mods.length
        ? Array.from(new Set(mods))
        : ["vehicle", "inventory"];

      return JSON.stringify({ modules: finalMods });
    } catch {
      return JSON.stringify({ modules: ["vehicle", "inventory"] });
    }
  }

  const resetProgress = () => {
    setPhase("idle");
    setPct(0);
    setLoaded(0);
    setTotal(0);
    setSpeed(0);
    setEta(null);
    controllerRef.current = null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!form.tenantCode || !form.name) {
      return Swal.fire(
        "Validation",
        "Tenant Code & Name are required",
        "warning"
      );
    }

    if (!DB_OK(form.databaseName)) {
      return Swal.fire(
        "Validation",
        "Invalid DB name. Use letters, numbers, underscore only. Max 64 chars.",
        "warning"
      );
    }

    if (needsUpload && !file) {
      return Swal.fire(
        "Validation",
        `${productInfo.short} template file is required`,
        "warning"
      );
    }

    if (file) {
      const n = file.name.toLowerCase();
      const allowedExts = productInfo.accept.split(",");
      const okExt = allowedExts.some((ext) => n.endsWith(ext));

      if (!okExt) {
        return Swal.fire(
          "Validation",
          `Only ${allowedExts.join(", ")} files allowed for ${productInfo.short}`,
          "warning"
        );
      }
    }

    const featuresJson = parseAndNormalizeFeatures(form.featuresJson);

    const fd = new FormData();

    if (file) {
      fd.append("sql", file);
    }

    fd.append("tenantCode", uc(form.tenantCode));
    fd.append("name", form.name.trim());
    fd.append("databaseName", form.databaseName.trim());
    fd.append("domain", form.domain.trim());
    fd.append("plan", form.plan);
    fd.append("maxUsers", String(form.maxUsers));
    fd.append("expiresOn", form.expiresOn || "");
    fd.append("graceDays", String(form.graceDays));
    fd.append("featuresJson", featuresJson);
    fd.append("product", form.product);
    fd.append("dbEngine", productInfo.dbEngine);
    fd.append(
      "usesServerTemplate",
      productInfo.usesServerTemplate ? "1" : "0"
    );

    const controller = new AbortController();
    controllerRef.current = controller;

    setPhase(file ? "uploading" : "processing");
    setPct(0);
    setLoaded(0);
    setTotal(0);
    setSpeed(0);
    setEta(null);
    tickRef.current = {
      t0: performance.now(),
      lastT: performance.now(),
      lastLoaded: 0,
    };

    try {
      await axios.post("https://superadmin.nissiindia.com/api/tenants/provision", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        signal: controller.signal,
        onUploadProgress: (evt) => {
          if (!file || !evt) return;

          const { loaded: l = 0, total: tt = 0 } = evt;

          setLoaded(l);
          setTotal(tt);

          if (tt > 0) {
            const p = Math.min(100, Math.round((l * 100) / tt));
            setPct(p);

            const now = performance.now();
            const dt = (now - tickRef.current.lastT) / 1000;
            const dB = l - tickRef.current.lastLoaded;

            if (dt > 0 && dB >= 0) {
              const instSpeed = dB / dt;

              setSpeed((prev) =>
                prev ? prev * 0.7 + instSpeed * 0.3 : instSpeed
              );

              if (instSpeed > 0) {
                const rem = tt - l;
                setEta(Math.max(0, Math.round(rem / instSpeed)));
              }
            }

            tickRef.current.lastT = now;
            tickRef.current.lastLoaded = l;

            if (p >= 100) {
              setPhase("processing");
            }
          }
        },
      });

      await Swal.fire(
        "Success",
        "Tenant created and database imported.",
        "success"
      );

      nav("/superAdmin/tenant");
    } catch (e2) {
      if (axios.isCancel?.(e2) || e2.name === "CanceledError") {
        Swal.fire("Cancelled", "Upload cancelled.", "info");
      } else {
        const msg =
          e2?.response?.data?.error || e2.message || "Provision failed";
        Swal.fire("Error", msg, "error");
      }
    } finally {
      resetProgress();
    }
  };

  const cancelUpload = () => {
    if (phase !== "uploading") return;

    try {
      controllerRef.current?.abort();
    } catch {}
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 text-white">
            <div className="px-4 sm:px-6 lg:px-8 py-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/30">
                    <Database className="h-5 w-5" />
                  </div>

                  <div>
                    <h1 className="text-2xl font-semibold leading-tight">
                      Add Tenant ({productInfo.short})
                    </h1>
                    <p className="text-white/80 text-sm">
                      Provision a {productInfo.short} tenant and import the
                      database template
                    </p>
                  </div>
                </div>

                <div className="text-sm text-white/90">
                  <Time />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <Server className="h-4 w-4" />
                  {productInfo.usesServerTemplate
                    ? `Server Template (${productInfo.short})`
                    : `Upload & Import (${productInfo.short})`}
                </span>

                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <ShieldCheck className="h-4 w-4" />
                  Validation & safeguards
                </span>
              </div>
            </div>
          </div>

          {phase !== "idle" && (
            <div className="px-4 sm:px-6 lg:px-8 py-4 bg-slate-900 text-white">
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-baseline gap-3">
                  <strong className="text-white">
                    {phase === "uploading"
                      ? "Uploading..."
                      : "Processing... creating tenant database"}
                  </strong>

                  {phase === "uploading" ? (
                    <span className="text-xs text-white/80">
                      {fmtBytes(loaded)} / {fmtBytes(total)} - {pct}%
                      {speed > 0 ? ` - ${fmtBytes(speed)}/s` : ""}
                      {eta != null ? ` - ~${eta}s left` : ""}
                    </span>
                  ) : (
                    <span className="text-xs text-white/80">
                      Server template/import is running. Please wait...
                    </span>
                  )}
                </div>

                <div className="h-2 w-full overflow-hidden rounded-full bg-white/20">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 transition-[width] duration-150"
                    style={{ width: `${phase === "uploading" ? pct : 100}%` }}
                  />
                </div>

                {phase === "uploading" && (
                  <div>
                    <button
                      type="button"
                      onClick={cancelUpload}
                      className="inline-flex items-center gap-2 rounded-lg bg-rose-500 px-3 py-1.5 text-white shadow hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-white/30"
                    >
                      <XCircle className="h-4 w-4" />
                      Cancel upload
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <form
              onSubmit={onSubmit}
              className="w-full"
              style={{
                opacity: disabled ? 0.7 : 1,
                pointerEvents: disabled ? "none" : "auto",
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3">
                    <Hash className="h-4 w-4 text-indigo-600" />
                    <h3 className="text-sm font-semibold text-slate-800">
                      Tenant Info
                    </h3>
                  </div>

                  <div className="p-4 space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Tenant Code *
                      </label>
                      <input
                        value={form.tenantCode}
                        onChange={(e) =>
                          setField("tenantCode", uc(e.target.value))
                        }
                        placeholder="OMEGA"
                        disabled={disabled}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Name *
                      </label>
                      <input
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        placeholder="Omega Tech"
                        disabled={disabled}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Product
                      </label>

                      <div className="flex gap-2">
                        {PRODUCTS.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setField("product", p.id)}
                            disabled={disabled}
                            className={`flex-1 rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                              form.product === p.id
                                ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                : "border-slate-300 bg-white text-slate-600 hover:border-indigo-300"
                            }`}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-teal-50 px-4 py-3">
                    <Database className="h-4 w-4 text-cyan-600" />
                    <h3 className="text-sm font-semibold text-slate-800">
                      Database ({productInfo.short})
                    </h3>
                  </div>

                  <div className="p-4 space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Database Name *
                      </label>
                      <input
                        value={form.databaseName}
                        onChange={(e) =>
                          setField("databaseName", e.target.value)
                        }
                        placeholder={`${productInfo.dbPrefix}omega`}
                        disabled={disabled}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />

                      <p className="mt-1 text-xs text-slate-500">
                        Alphanumeric & underscore only. Max 64 chars. Default
                        prefix is{" "}
                        <code className="font-mono">
                          {productInfo.dbPrefix}
                        </code>
                        .
                      </p>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Domain (optional)
                      </label>

                      <div className="relative">
                        <Globe className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          value={form.domain}
                          onChange={(e) => setField("domain", e.target.value)}
                          placeholder={productInfo.domain}
                          disabled={disabled}
                          className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 border-b border-slate-200 bg-gradient-to-r from-fuchsia-50 to-pink-50 px-4 py-3">
                    <ShieldCheck className="h-4 w-4 text-fuchsia-600" />
                    <h3 className="text-sm font-semibold text-slate-800">
                      Plan & Limits ({productInfo.short})
                    </h3>
                  </div>

                  <div className="p-4 space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Plan *
                      </label>
                      <select
                        value={form.plan}
                        onChange={(e) => setField("plan", e.target.value)}
                        disabled={disabled}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-200"
                      >
                        {PLANS.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Max Users *
                      </label>

                      <div className="relative">
                        <Users className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          type="number"
                          min={0}
                          value={form.maxUsers}
                          onChange={(e) =>
                            setField("maxUsers", Number(e.target.value))
                          }
                          disabled={disabled}
                          className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-200"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3">
                    <CalendarDays className="h-4 w-4 text-amber-600" />
                    <h3 className="text-sm font-semibold text-slate-800">
                      Expiry & Grace ({productInfo.short})
                    </h3>
                  </div>

                  <div className="p-4 space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Expires On (optional)
                      </label>
                      <input
                        type="date"
                        value={form.expiresOn}
                        onChange={(e) => setField("expiresOn", e.target.value)}
                        disabled={disabled}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Grace Days *
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={60}
                        value={form.graceDays}
                        onChange={(e) =>
                          setField("graceDays", Number(e.target.value))
                        }
                        disabled={disabled}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
                      />
                    </div>
                  </div>
                </div>

                <div className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-sky-50 px-4 py-3">
                    {productInfo.usesServerTemplate ? (
                      <Server className="h-4 w-4 text-sky-600" />
                    ) : (
                      <UploadCloud className="h-4 w-4 text-sky-600" />
                    )}

                    <h3 className="text-sm font-semibold text-slate-800">
                      {productInfo.usesServerTemplate
                        ? `${productInfo.short} Server Template`
                        : `${productInfo.short} Template (${productInfo.accept})`}
                    </h3>
                  </div>

                  <div className="p-4">
                    {productInfo.usesServerTemplate ? (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-5">
                        <div className="flex items-start gap-3">
                          <Server className="h-5 w-5 text-emerald-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-emerald-800">
                              Server template will be used automatically.
                            </p>
                            <p className="mt-1 text-xs text-emerald-700">
                              No SQL upload is required for {productInfo.long}.
                              The backend will select the approved template,
                              clean database-level statements, create the tenant
                              database, and import it.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          accept={productInfo.accept}
                          onChange={(e) =>
                            setFile(e.target.files?.[0] || null)
                          }
                          disabled={disabled}
                          className="w-full rounded-lg border border-dashed border-slate-300 bg-white px-3 py-8 text-slate-900 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-white hover:file:bg-slate-800"
                        />

                        <p className="mt-2 text-xs text-slate-500">
                          {productInfo.uploadText} for {productInfo.long}. Avoid{" "}
                          <code className="font-mono">CREATE DATABASE</code> /{" "}
                          <code className="font-mono">USE</code> statements.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 mt-6 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t border-slate-200">
                <div className="px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center gap-3 sm:justify-between">
                  <p className="text-xs text-slate-500">
                    {productInfo.usesServerTemplate
                      ? "Backend will use the approved server template and create the tenant database."
                      : "The banner shows upload progress. After 100%, the server completes the import."}
                  </p>

                  <div className="flex items-center gap-2">
                    <button
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      type="submit"
                      disabled={disabled}
                    >
                      <Save className="h-4 w-4" />
                      Create & Import
                    </button>

                    <button
                      className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-white shadow hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                      type="button"
                      onClick={() => window.history.back()}
                      disabled={disabled}
                    >
                      <XCircle className="h-4 w-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <p className="mt-3 text-xs text-slate-600">
              Tip: PATROL and SENTINEL use server templates. VISIT currently
              requires upload.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}